import { useCallback, useEffect, useRef, useState } from 'react'
import { Typography, styled } from '@mui/material'
import { useToggle } from 'usehooks-ts'
import { useHistoryCustomBack } from 'weblite-router'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { t } from '$i18n'
import { BackButton } from '$common/buttons'
import { nothing } from '$helper/functions/utils'
import { BottomDrawer } from '$common/bottom-drawer'
import { getDateOfNDaysLater } from '$comp/modals/date-picker/date-picker.helper'
import { ChannelCallType } from '../choose-call-type/choose-channel-call'
import { GroupCallType } from '../choose-call-type/choose-group-call'
import { dateWithOffset } from '$logic/client-device/time-offset.service'
import { StartNowForm } from '$comp/modals/create-hall-call/choose-call-time/components/forms/start-now'
import { ScheduleForm } from '$comp/modals/create-hall-call/choose-call-time/components/forms/schedule'
import { EditScheduleForm } from '$comp/modals/create-hall-call/choose-call-time/components/forms/edit-schedule'

export type HallCallType = ChannelCallType | GroupCallType
const MINUTE_IN_MS = 60 * 1000

export interface HallCallTime {
  time: Date
  shouldSendNotificationMessage: boolean
  title: string
  shouldStartNow: boolean
}

function isDateNow(date: Date) {
  const now = dateWithOffset()
  return +date - +now < MINUTE_IN_MS
}

export interface Props {
  isOpen?: boolean
  callType: HallCallType
  onConfirm: (hallCallTime: HallCallTime) => unknown
  onCancel: () => unknown
  // hallType: 'CHANNEL' | 'GROUP'
  defaultTitle?: string
  // defaultShouldSendNotification?: boolean
  onBack?: () => void
  editMode?: boolean
  defaultTime?: Date
  onDelete?: () => unknown
  titlePlaceholder?: string
}

export function ChooseHallCallTime({
  isOpen = false,
  onConfirm,
  onCancel,
  callType,
  defaultTitle = '',
  defaultTime,
  onBack = closeCurrentModal,
  editMode = false,
  onDelete = nothing,
  titlePlaceholder,
}: Props) {
  const startDateRef = useRef(new Date(dateWithOffset()))
  const [time, setTime] = useState(defaultTime || startDateRef.current)
  const [scheduling, toggleScheduling] = useToggle(false)

  const [title, setTitle] = useState(defaultTitle)
  const [titleError, setTitleError] = useState('')
  const [titleErrorVisible, setTitleErrorVisible] = useState(false)

  const dialogTitle = t(
    `chooseHallCallTimeModal.${editMode ? 'editingCall' : scheduling ? 'schedulingCall' : 'creatingCall'}.${
      callType === 'group_call' ? 'meeting' : callType
    }`,
  )

  const handleBack = useCallback(() => {
    if (scheduling) toggleScheduling()
    else onBack()
  }, [scheduling, onBack, toggleScheduling])

  useHistoryCustomBack(handleBack, [handleBack], Boolean(onBack) || scheduling)

  useEffect(() => {
    if (title && title.length < 3)
      setTitleError(t(`chooseHallCallTimeModal.titleError.${callType}`))
    else setTitleError('')
  }, [title, callType])

  const handleToggleScheduling = useCallback(() => {
    setTitleErrorVisible(true)
    if (titleError) return
    toggleScheduling()
  }, [toggleScheduling, titleError])

  const handleSchedule = useCallback(() => {
    setTitleErrorVisible(true)

    if (titleError) return

    onConfirm({
      time,
      shouldSendNotificationMessage: false,
      title,
      shouldStartNow: isDateNow(time),
    })
  }, [time, title, onConfirm, titleError])

  const handleStartNow = useCallback(() => {
    setTitleErrorVisible(true)
    if (titleError) return

    onConfirm({
      time: startDateRef.current,
      shouldSendNotificationMessage: false,
      title,
      shouldStartNow: true,
    })
  }, [title, onConfirm, titleError])

  const titleComponent = (
    <TitleContainer>
      <BackButton onClick={handleBack} color="secondary700" />
      <Typography variant="subtitle1" color="secondary700">
        {dialogTitle}
      </Typography>
    </TitleContainer>
  )
  return (
    <>
      <BottomDrawer isOpen={Boolean(isOpen) && !scheduling} onClose={onCancel}>
        <Root>
          {titleComponent}
          {editMode ? (
            <EditScheduleForm
              startDate={startDateRef.current}
              endDate={getDateOfNDaysLater(7)}
              onChangeDate={setTime}
              selectedDate={time}
              title={title}
              onChangeTitle={setTitle}
              onDelete={onDelete}
              onConfirm={handleSchedule}
              titleError={titleErrorVisible ? titleError : ''}
              titlePlaceholder={titlePlaceholder}
            />
          ) : (
            <StartNowForm
              title={title}
              titlePlaceholder={titlePlaceholder}
              onChangeTitle={setTitle}
              onStartNow={handleStartNow}
              onSchedule={handleToggleScheduling}
              titleError={titleErrorVisible ? titleError : ''}
            />
          )}
        </Root>
      </BottomDrawer>
      <BottomDrawer onClose={onCancel} isOpen={Boolean(isOpen) && scheduling}>
        <Root>
          {titleComponent}

          <ScheduleForm
            startDate={startDateRef.current}
            endDate={getDateOfNDaysLater(7)}
            onChangeDate={setTime}
            selectedDate={time}
            onSubmit={handleSchedule}
          />
        </Root>
      </BottomDrawer>
    </>
  )
}

const Root = styled('div')`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 16px;
  .MuiDivider-root {
    border-color: ${({ theme }) => theme.palette.neutral400};
  }
`

const TitleContainer = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: -8px;
  margin-bottom: 20px;
`

// const RadioButtonContainer = styled('div')`
//   display: flex;
//   align-items: center;
//   justify-content: space-between;
//   padding: 14px 0;
//   margin-bottom: 24px;
// `
